import { FC, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { OrganizationMarketplaceVehicle } from '@spike/marketplace-model';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import DescriptionText from 'components/UI/V3/DescriptionText/DescriptionText';
import { AlertMessage } from 'components/UI/V3/AlertMessage';
import { ChipSelector, Element } from 'components/Service/ChipSelector';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import Service from 'model/Service';
import styles from './VehiclesSelectionSection.module.scss';

interface VehiclesSelectionSectionProps {
    marketplaces: Service['marketplaces'];
    onChangeMarketplaces: (marketplaces: Service['marketplaces']) => void;
    className?: string;
}

const vehicleToElement = (vehicle: OrganizationMarketplaceVehicle): Element<number> => ({
    id: vehicle.id,
    name: `${vehicle.name} (Max ${vehicle.maxPets} pets)`
});

const buildVehicleElements = (vehicles: Array<OrganizationMarketplaceVehicle>): Array<Element<number>> =>
    vehicles
        .filter(vehicle => vehicle.active)
        .map(vehicleToElement)
        .sort((left, right) => left.name.toLocaleLowerCase().localeCompare(right.name.toLocaleLowerCase()));

const buildSelectedVehicleElements = (
    vehicles: Array<OrganizationMarketplaceVehicle>,
    selectedVehicleIds: Array<number>
): Array<Element<number>> => vehicles.filter(vehicle => selectedVehicleIds.includes(vehicle.id)).map(vehicleToElement);

export const VehiclesSelectionSection: FC<VehiclesSelectionSectionProps> = ({
    marketplaces,
    onChangeMarketplaces,
    className
}) => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const [expandedMarketplaceIds, setExpandedMarketplaceIds] = useState<Array<number>>([]);

    const selectedMarketplaces = useMemo(
        () =>
            marketplaces
                .map(serviceMarketplace => {
                    const servicesInfo = organizationServicesInfo.find(
                        info => info.marketplace.id === serviceMarketplace.marketplaceId
                    );

                    if (!servicesInfo) {
                        return null;
                    }

                    return {
                        serviceMarketplace,
                        servicesInfo
                    };
                })
                .filter(
                    (
                        marketplace
                    ): marketplace is {
                        serviceMarketplace: Service['marketplaces'][number];
                        servicesInfo: (typeof organizationServicesInfo)[number];
                    } => marketplace !== null
                )
                .sort((left, right) =>
                    left.servicesInfo.marketplace.name
                        .toLocaleLowerCase()
                        .localeCompare(right.servicesInfo.marketplace.name.toLocaleLowerCase())
                ),
        [organizationServicesInfo, marketplaces]
    );

    const hasAnyVehicles = selectedMarketplaces.some(({ servicesInfo }) => (servicesInfo.vehicles?.length ?? 0) > 0);

    const marketplaceVehicleSelectionChangeHandler =
        (marketplaceId: number) =>
        (elements: Array<Element<number>>): void => {
            onChangeMarketplaces(
                marketplaces.map(marketplace =>
                    marketplace.marketplaceId === marketplaceId
                        ? { ...marketplace, vehicleIds: elements.map(element => element.id) }
                        : marketplace
                )
            );
        };

    const toggleMarketplaceExpanded = (marketplaceId: number) => {
        setExpandedMarketplaceIds(prev =>
            prev.includes(marketplaceId) ? prev.filter(id => id !== marketplaceId) : [...prev, marketplaceId]
        );
    };

    return (
        <div className={`${styles.section} ${className || ''}`}>
            <div className={styles.label}>
                <Label required>Vehicles</Label>
            </div>
            <DescriptionText text="Choose the applicable vehicles for each location." />

            {!hasAnyVehicles && (
                <div className={styles.noVansAvailable}>
                    <AlertMessage
                        icon={faInfoCircle}
                        iconColor="#BAA997"
                        bdColor="#BCB8AE"
                        bgColor="#F8F5F1"
                    >
                        <span>
                            You don&apos;t have any vehicles set up yet. Please{' '}
                            <a href="/transportation?tab=vehicles">add a vehicle</a> before completing your setup.
                        </span>
                    </AlertMessage>
                </div>
            )}

            {hasAnyVehicles && (
                <div className={styles.vehiclesContainer}>
                    {selectedMarketplaces.map(({ serviceMarketplace, servicesInfo }) => {
                        const vehicles = servicesInfo.vehicles ?? [];
                        const activeVehicles = vehicles.filter(vehicle => vehicle.active);
                        const selectableElements = buildVehicleElements(vehicles);
                        const selectedElements = buildSelectedVehicleElements(
                            vehicles,
                            serviceMarketplace.vehicleIds ?? []
                        );
                        const selectedCount = selectedElements.length;
                        const totalCount = selectableElements.length;
                        const isExpanded = expandedMarketplaceIds.includes(serviceMarketplace.marketplaceId);

                        return (
                            <div
                                key={serviceMarketplace.marketplaceId}
                                className={styles.marketplaceVehiclesSection}
                            >
                                <div
                                    className={styles.marketplaceHeader}
                                    onClick={() => toggleMarketplaceExpanded(serviceMarketplace.marketplaceId)}
                                >
                                    <div className={styles.marketplaceHeaderLeft}>
                                        <p className={styles.marketplaceName}>{servicesInfo.marketplace.name}</p>
                                        <p className={styles.marketplaceCount}>
                                            ({selectedCount}/{totalCount})
                                        </p>
                                    </div>
                                    <FontAwesomeIcon
                                        icon={isExpanded ? faChevronUp : faChevronDown}
                                        className={styles.marketplaceChevron}
                                    />
                                </div>
                                <div className={styles.marketplaceNameDivider} />
                                {isExpanded && (
                                    <div className={styles.marketplaceVehiclesContent}>
                                        {activeVehicles.length > 0 ? (
                                            <ChipSelector
                                                id={`transportation_vehicles_selector_${serviceMarketplace.marketplaceId}`}
                                                searchLabel="Search vehicles"
                                                elements={selectableElements}
                                                selectedElements={selectedElements}
                                                changeSelection={marketplaceVehicleSelectionChangeHandler(
                                                    serviceMarketplace.marketplaceId
                                                )}
                                            />
                                        ) : (
                                            <p className={styles.vehiclesNotDefined}>Vehicles not defined</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
